import { useEffect } from 'react'
import { Box, Grid } from '@mui/material'
import { useFormikContext } from 'formik'
import { t } from 'i18next'

import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import InfoBar from 'src/components/shared/ui/InfoBar/InfoBar'
import { InputField, AutoCompleteField } from 'src/components/shared/ui/form/fields'
import FieldsetGroup from 'src/components/shared/ui/form/fields/FieldsetGroup/FieldsetGroup'
import AddressForm from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/forms/AddressForm'
import {
  checkTipoPersonaFisica,
  getLabelByTipoPersona
} from 'src/components/views/EscritosLexnet/components/shared/components/Intervinientes/components/types'
import { useTipoMensajeContext } from 'src/contexts/TipoMensajeContext'
import { TipoMensaje } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'

import {
  useGetTipoPersona,
  useGetTipoDocumentoIdentificativo
} from 'src/hooks/fetch/useCatalogoGeneralFilters'
import useFetching from 'src/hooks/useFetching'
import {
  Orden,
  TipoPersona,
  OrganoJudicial,
  TipoIntervencion,
  TipoDocumentoIdentificativo
} from 'src/interfaces/Catalog'
import { Interviniente } from 'src/interfaces/EscritosLegales'
import { getTipoIntervencion } from 'src/services/CatalogService'

import BankAccount from '../forms/BankAccount'
import TipoPersonaFormFields from './TipoPersonaFormFields'
import { INDOCUMENTADO_CODIGO } from './types'

const TIPO_INTERVENCION_MONITORIO = ['SOL', 'DDO', 'COD']

function GetFieldsByTipoPersona({ monitorio = false }: { monitorio?: boolean }): JSX.Element {
  const { values } = useFormikContext<Interviniente>()
  const tipoPersona = values[FormField.INTERVINIENTE_PERSONA_TIPO] as TipoPersona

  const label = getLabelByTipoPersona(tipoPersona)

  if (tipoPersona && checkTipoPersonaFisica(tipoPersona)) {
    return (
      <>
        <Grid item xs={12} sm={6} lg={4}>
          <InputField id={FormField.INTERVINIENTE_NOMBRE} label={label} required maxLength={200} />
        </Grid>
        {monitorio && (
          <Grid item xs={12} sm={6} lg={4}>
            <InputField
              id={FormField.INTERVINIENTE_TELEFONO}
              type="tel"
              label={t('form.field.telefono')}
              maxLength={12}
            />
          </Grid>
        )}
        {monitorio && (
          <Grid item xs={12} sm={6} lg={4}>
            <InputField
              id={FormField.INTERVINIENTE_EMAIL}
              label={t('form.field.email')}
              maxLength={50}
              type="email"
            />
          </Grid>
        )}
      </>
    )
  }

  return (
    <>
      <Grid item xs={12} sm={6} lg={4}>
        <InputField id={FormField.INTERVINIENTE_NOMBRE} label={label} required maxLength={50} />
      </Grid>
      <TipoPersonaFormFields monitorio={monitorio} />
    </>
  )
}

type IntervinienteFormProps = {
  orden?: Orden | null
  organo?: OrganoJudicial | null
}

export default function IntervinienteForm(props: IntervinienteFormProps): JSX.Element {
  const { orden = null, organo = null } = props
  const { values, setFieldValue, validateForm } = useFormikContext<Interviniente>()
  const tipoPersona = values[FormField.INTERVINIENTE_PERSONA_TIPO] as TipoPersona
  const tipoPersonaData = useGetTipoPersona(FormField.INTERVINIENTE_PERSONA_TIPO)

  const tipoIdentificacion = values[FormField.INTERVINIENTE_IDENTIFICACION_TIPO]?.codigo ?? null

  const { tipoMensaje } = useTipoMensajeContext()

  const tipoIdentificacionData = useGetTipoDocumentoIdentificativo(
    FormField.INTERVINIENTE_IDENTIFICACION_TIPO,
    tipoPersona
  )

  const tipoIntervencion = useFetching({
    key: [FormField.INTERVINIENTE_IDENTIFICACION_TIPO],
    fetchFn: () => getTipoIntervencion({ orden, organo })
  })

  function handleOnChange() {
    setFieldValue(FormField.INTERVINIENTE_IDENTIFICACION_TIPO, null, false)
    setFieldValue(FormField.INTERVINIENTE_IDENTIFICACION, '', false)
    setFieldValue(FormField.INTERVINIENTE_NOMBRE, '', false)
    setFieldValue(FormField.INTERVINIENTE_PRIMER_APELLIDO, '', false)
    setFieldValue(FormField.INTERVINIENTE_SEGUNDO_APELLIDO, '', false)
    setFieldValue(FormField.INTERVINIENTE_FECHA_NACIMIENTO, null, false)
    setFieldValue(FormField.INTERVINIENTE_SEXO, null, false)
    setFieldValue(FormField.INTERVINIENTE_NACIONALIDAD, null, false)
    setFieldValue(FormField.INTERVINIENTE_PAIS_NACIMIENTO, null, false)
    setFieldValue(FormField.INTERVINIENTE_LUGAR_NACIMIENTO, '', false)
    setFieldValue(FormField.INTERVINIENTE_NOMBRE_PADRE, '', false)
    setFieldValue(FormField.INTERVINIENTE_NOMBRE_MADRE, '', false)
    setFieldValue(FormField.INTERVINIENTE_IDENTIFICACION_TIPO, null, false)
    setFieldValue(FormField.INTERVINIENTE_IDENTIFICACION, '', false)
    setFieldValue(FormField.INTERVINIENTE_EMAIL, '', false)
    setFieldValue(FormField.INTERVINIENTE_TELEFONO, '', false)
    setTimeout(() => validateForm)
  }

  function filterTipoIntervencionMonitorio(tipoIntervencion: TipoIntervencion): boolean {
    return TIPO_INTERVENCION_MONITORIO.includes(tipoIntervencion.codigo)
  }

  function filterTipoIndentificacionMonitorio(
    tipoIdentificacion: TipoDocumentoIdentificativo
  ): boolean {
    return tipoIdentificacion.codigo !== 'I'
  }

  useEffect(() => {
    if (tipoIdentificacion === INDOCUMENTADO_CODIGO) {
      setFieldValue(FormField.INTERVINIENTE_IDENTIFICACION, '', false)
    }
  }, [tipoIdentificacion, setFieldValue])

  return (
    <>
      <FieldsetGroup legend={t('form.legend.datos_interviniente')}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={4}>
            <AutoCompleteField
              id={FormField.INTERVINIENTE_PERSONA_TIPO}
              label={t('form.field.tipo_persona')}
              items={tipoPersonaData}
              itemName="descripcion"
              itemKey="codigo"
              required
              onChange={handleOnChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <AutoCompleteField
              id={FormField.INTERVINIENTE_INTERVENCION_TIPO}
              label={t('form.field.tipo_intervencion')}
              items={
                tipoMensaje === TipoMensaje.MONITORIO
                  ? tipoIntervencion.data?.filter(filterTipoIntervencionMonitorio)
                  : tipoIntervencion.data
              }
              itemName="descripcion"
              required
              loading={tipoIntervencion.isLoading}
              getOptionLabelCallback={(option: TipoIntervencion) =>
                `${option.descripcion} [${option.codigo}]`
              }
              renderOptionCallback={(props, option: TipoIntervencion) => (
                <li {...props}>
                  {option.descripcion} [{option.codigo}]
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <AutoCompleteField
              id={FormField.INTERVINIENTE_IDENTIFICACION_TIPO}
              items={
                tipoMensaje === TipoMensaje.MONITORIO
                  ? tipoIdentificacionData?.filter(filterTipoIndentificacionMonitorio)
                  : tipoIdentificacionData
              }
              label={t('form.field.tipo_identificacion')}
              itemName="descripcion"
              disabled={!tipoPersona}
              required
            />
          </Grid>
          {tipoIdentificacion !== INDOCUMENTADO_CODIGO && (
            <>
              <Grid item xs={12} sm={6} lg={4}>
                <InputField
                  id={FormField.INTERVINIENTE_IDENTIFICACION}
                  label={t('form.field.numero')}
                  required
                  maxLength={12}
                  disabled={!tipoIdentificacion}
                />
              </Grid>
            </>
          )}
          <GetFieldsByTipoPersona monitorio={tipoMensaje === TipoMensaje.MONITORIO} />
        </Grid>
      </FieldsetGroup>

      {tipoMensaje === TipoMensaje.MONITORIO && (
        <FieldsetGroup legend={t('form.legend.cuenta_bancaria')}>
          <BankAccount />
        </FieldsetGroup>
      )}
      <FieldsetGroup legend={t('form.legend.direccion_interviniente')}>
        <Box sx={{ marginBottom: '16px' }}>
          <InfoBar
            message={t('form.warning.domicilio_espana')}
            type={AlertType.INFO}
            showCloseIcon={false}
          />
        </Box>
        <AddressForm />
      </FieldsetGroup>
    </>
  )
}
